import type {
	RegisteredRouter,
	RouteIds,
	ToPathOption,
} from "@tanstack/react-router";
import { useRouter, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";

interface UseSuccessQueryOptions {
	from: RouteIds<RegisteredRouter["routeTree"]>;
	matcher: string;
	redirect: ToPathOption;
	successMessage?: string;
	onSuccess?: () => void | Promise<void>;
}

export function useSuccessQuery(options: UseSuccessQueryOptions) {
	const router = useRouter();
	const { from, matcher, redirect, successMessage, onSuccess } = options;

	const searchParam = useSearch({
		from,
		shouldThrow: false,
	});

	useEffect(() => {
		if (
			searchParam &&
			"success" in searchParam &&
			searchParam.success === matcher
		) {
			// Run onSuccess first (e.g. refetch user session) so that
			// router.invalidate() picks up fresh data in loaders.
			Promise.resolve(onSuccess?.())
				.then(() => router.invalidate())
				.then(() => router.navigate({ to: redirect }))
				.then(() => {
					if (successMessage) {
						toast.success(successMessage, {
							id: matcher,
						});
					}
				});
		}
	}, [searchParam, router, matcher, redirect, successMessage, onSuccess]);
}
